import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { CURRENT } from '../config/networks';

const WalletContext = createContext(null);
export const useWallet = () => useContext(WalletContext);

export function WalletProvider({ children }) {
  const [address, setAddress] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);

  // guard so the auto-switch never re-enters and loops
  const switchingRef = useRef(false);

  const isConnected = !!address;
  const isCorrectNetwork = chainId === CURRENT.chainId;

  const buildProvider = useCallback(() => {
    if (!window.ethereum) return null;
    // 'any' lets the provider tolerate network changes without throwing
    const p = new ethers.providers.Web3Provider(window.ethereum, 'any');
    setProvider(p);
    setSigner(p.getSigner());
    return p;
  }, []);

  // Ask the wallet to move to the correct chain. No reload — state updates
  // come from the 'chainChanged' handler below.
  const ensureNetwork = useCallback(async () => {
    if (!window.ethereum) return false;
    if (switchingRef.current) return false;

    const currentHex = await window.ethereum.request({ method: 'eth_chainId' });
    if (parseInt(currentHex, 16) === CURRENT.chainId) return true;

    switchingRef.current = true;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: CURRENT.chainIdHex }],
      });
      return true;
    } catch (err) {
      // 4902 = chain not added in the wallet yet
      if (err && (err.code === 4902 || (err.data && err.data.originalError && err.data.originalError.code === 4902))) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: CURRENT.chainIdHex,
              chainName: CURRENT.chainName,
              nativeCurrency: CURRENT.nativeCurrency,
              rpcUrls: CURRENT.rpcUrls,
              blockExplorerUrls: CURRENT.blockExplorerUrls,
            }],
          });
          return true;
        } catch (_) {
          return false;
        }
      }
      return false;
    } finally {
      switchingRef.current = false;
    }
  }, []);

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      throw new Error('No wallet found. Open this site inside Trust Wallet or MetaMask.');
    }
    setConnecting(true);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      buildProvider();
      setAddress(accounts[0] ? ethers.utils.getAddress(accounts[0]) : null);

      const hex = await window.ethereum.request({ method: 'eth_chainId' });
      setChainId(parseInt(hex, 16));

      // if they connected on the wrong chain, move them automatically
      await ensureNetwork();
      return accounts[0];
    } finally {
      setConnecting(false);
    }
  }, [buildProvider, ensureNetwork]);

  const disconnect = useCallback(() => {
    setAddress(null);
    setSigner(null);
  }, []);

  // attach listeners ONCE. handlers update state only — never reload,
  // never call connect() recursively.
  useEffect(() => {
    if (!window.ethereum) return;

    const onAccountsChanged = (accounts) => {
      if (!accounts || accounts.length === 0) {
        setAddress(null);
        setSigner(null);
      } else {
        setAddress(ethers.utils.getAddress(accounts[0]));
        if (provider) setSigner(provider.getSigner());
      }
    };

    const onChainChanged = (hex) => {
      const id = parseInt(hex, 16);
      setChainId(id);
      // rebuild signer against the new chain (no reload)
      const p = buildProvider();
      if (p && address) setSigner(p.getSigner());
      // if they manually moved to the wrong chain, nudge back once
      if (id !== CURRENT.chainId) ensureNetwork();
    };

    window.ethereum.on('accountsChanged', onAccountsChanged);
    window.ethereum.on('chainChanged', onChainChanged);

    // silent reconnect if already authorized
    (async () => {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts && accounts.length) {
          buildProvider();
          setAddress(ethers.utils.getAddress(accounts[0]));
          const hex = await window.ethereum.request({ method: 'eth_chainId' });
          setChainId(parseInt(hex, 16));
        }
      } catch (_) {}
    })();

    return () => {
      window.ethereum.removeListener('accountsChanged', onAccountsChanged);
      window.ethereum.removeListener('chainChanged', onChainChanged);
    };
  }, []);

  const value = {
    address, chainId, isConnected, isCorrectNetwork, connecting,
    provider, signer, connect, disconnect, ensureNetwork,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
